import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft, Share2, MapPin, Building,
    CheckCircle, X, Mail, Globe, Copy, Briefcase, Zap, Shield, FileText, Check, Banknote
} from 'lucide-react';
import Swal from 'sweetalert2';
import { showSuccess, showError, showLoading, closeLoading, showConfirm } from '../../utils/Alert';
import Loader from '../shared/Loader';
import { API_URL } from '../../config';
import { AuthContext } from '../../context/AuthContext';
import ShareModal from '../shared/ShareModal';
import '../Student/views/CourseDetail.css';

const PublicJobDetail = ({ jobId: propJobId, onClose, onApply, appStatus }) => {
    const { jobId: paramJobId } = useParams();
    const jobId = propJobId || paramJobId;
    const navigate = useNavigate();
    const { user } = React.useContext(AuthContext);

    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('overview');
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    const tabsRef = useRef(null);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        // Only hide body overflow if we are in modal/portal mode
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        fetchDetails();
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [jobId]);

    const fetchDetails = async () => {
        try {
            const res = await axios.get(`/api/public/jobs/${jobId}`);
            setJob(res.data);
            setLoading(false);
        } catch (err) {
            showError("Load Error", "Could not retrieve job details.");
            setLoading(false);
        }
    };

    const handleApply = async () => {
        if (onApply) {
            onApply();
            return;
        }

        if (!user) {
            navigate('/auth');
            return;
        }

        if (user.role === 'job_seeker' || user.role === 'student') {
            const path = user.role === 'job_seeker' ? '/jobseeker-dashboard' : '/student-dashboard';
            navigate(path);
            return;
        }

        Swal.fire({
            icon: 'warning',
            title: 'Candidate Account Required',
            html: `You're currently signed in as <strong>${user.role.replace('_', ' ')}</strong>.<br/>To apply for jobs, you need to be a <strong>Job Seeker</strong> or a <strong>Student</strong>.<br/><br/>Please register as a candidate to continue.`,
            confirmButtonText: 'Create Account',
            showCancelButton: true,
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#1a3a6b'
        }).then(r => { if (r.isConfirmed) navigate('/auth?type=job_seeker'); });
    };

    const handleClose = () => {
        if (onClose) onClose();
        else navigate(-1);
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        showSuccess('Link Copied!', 'Job link copied to clipboard.');
    };

    if (loading) return <Loader />;
    if (!job) return null;

    const sections = [
        { id: 'overview', label: 'Overview' },
        { id: 'company', label: 'About Company' }
    ];

    // Status map for banner (mirroring dashboards)
    const STATUS_MAP = {
        Pending: { label: 'Applied', color: '#92400E', bg: '#FEF3C7' },
        Shortlisted: { label: 'Shortlisted ✓', color: '#065F46', bg: '#D1FAE5' },
        Rejected: { label: 'Not Selected', color: '#B91C1C', bg: '#FEE2E2' },
        'Interview Scheduled': { label: 'Interview Scheduled', color: '#1D4ED8', bg: '#DBEAFE' },
        'Interview Completed': { label: 'Interview Completed', color: '#065F46', bg: '#D1FAE5' },
        Withdrawn: { label: 'Withdrawn', color: '#475569', bg: '#F1F5F9' }
    };

    const jobDetailContent = (
        <div className="course-detail-root-v2">

            {/* Share Modal Portal Layer */}
            <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                shareUrl={window.location.href}
                title={job.title}
            />

            {/* Floating Nav */}
            <div className="cd-floating-nav">
                <div className="cd-nav-left">
                    <button className="cd-nav-btn" onClick={handleClose}>
                        <ArrowLeft size={18} /> <span>{onClose ? 'Close' : 'Back'}</span>
                    </button>
                </div>
                <div className="cd-nav-right">
                    <button className="cd-nav-btn" onClick={() => setIsShareModalOpen(true)}>
                        <Share2 size={18} /> <span>Share</span>
                    </button>
                    {!appStatus && (
                        <button className="cd-nav-btn cd-enroll-btn" onClick={handleApply}>
                            Apply Now
                        </button>
                    )}
                </div>
            </div>

            <main className="cd-main-wrapper">
                <div className="cd-hero-block" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}>
                    <div className="cd-hero-decoration">
                        <div className="cd-hero-sparkle">✦</div>
                        <div className="cd-hero-sparkle s2">✦</div>
                    </div>

                    <div className="cd-hero-content">
                        <h1 className="cd-title">{job.title}</h1>
                        <div className="cd-instructor-badge">
                            {job.companyId?.logoUrl ? (
                                <img
                                    src={`${API_URL}${job.companyId.logoUrl}`}
                                    className="cd-company-logo-badge"
                                    alt={job.companyId?.name}
                                />
                            ) : (
                                <div className="cd-avatar-placeholder" style={{ background: '#3b82f6' }}>{job.company?.charAt(0) || 'C'}</div>
                            )}
                            <div className="cd-instructor-info">
                                <h4>{job.company || job.companyId?.name}</h4>
                                <p><MapPin size={14} style={{ display: 'inline', marginRight: '4px' }} /> {job.location}</p>
                            </div>
                        </div>
                        <div className="cd-tags">
                            <span className={`cd-tag ${job.type?.toLowerCase().replace(/\s+/g, '-')}`}>{job.type}</span>
                            <span className="cd-tag" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                Pay: {job.salaryRange || 'Competitive'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="cd-layout">
                    <div className="cd-left-column">
                        <div className="cd-tabs" ref={tabsRef}>
                            {sections.map(s => (
                                <div
                                    key={s.id}
                                    className={`cd-tab ${activeSection === s.id ? 'active' : ''}`}
                                    onClick={() => setActiveSection(s.id)}
                                >
                                    {s.label}
                                </div>
                            ))}
                        </div>

                        <div className="cd-tab-content-area">
                            {activeSection === 'overview' && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="cd-overview-area">
                                    {/* Application status banner (if already applied) */}
                                    {appStatus && STATUS_MAP[appStatus] && (
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: '12px',
                                            padding: '16px 20px', borderRadius: '12px', marginBottom: '24px',
                                            background: STATUS_MAP[appStatus].bg, borderLeft: `5px solid ${STATUS_MAP[appStatus].color}`,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                        }}>
                                            <div style={{ background: STATUS_MAP[appStatus].color, color: '#fff', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Check size={14} strokeWidth={3} />
                                            </div>
                                            <div>
                                                <p style={{ margin: 0, fontWeight: 800, color: STATUS_MAP[appStatus].color, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{STATUS_MAP[appStatus].label}</p>
                                                <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Your current application status for this position.</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="cd-tab-content-card">
                                        <h2>Job Description</h2>
                                        <p style={{ whiteSpace: 'pre-line' }}>{job.description}</p>

                                        {job.requirements && job.requirements.length > 0 && (
                                            <>
                                                <h3 style={{ marginTop: '20px' }}>Requirements</h3>
                                                <ul className="cd-prereq-list">
                                                    {job.requirements.map((req, i) => <li key={i}>{req}</li>)}
                                                </ul>
                                            </>
                                        )}
                                    </div>

                                    <div className="cd-full-width-section">
                                        <h2>Why Apply?</h2>
                                        <div className="cd-benefits-grid">
                                            <div className="cd-benefit">
                                                <Briefcase size={28} color="#1e88e5" />
                                                <h4>Career Growth</h4>
                                                <p>Join a fast-paced environment that accelerates your career trajectory.</p>
                                            </div>
                                            <div className="cd-benefit">
                                                <Zap size={28} color="#1e88e5" />
                                                <h4>Top Tier Compensation</h4>
                                                <p>Competitive base pay and excellent benefits packages.</p>
                                            </div>
                                            <div className="cd-benefit">
                                                <Shield size={28} color="#1e88e5" />
                                                <h4>Verified Employer</h4>
                                                <p>This job opening is verified directly by our recruiting team.</p>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {activeSection === 'company' && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="cd-tab-content-card">
                                    <h2>About {job.company || job.companyId?.name}</h2>
                                    <div className="cd-instructor-view" style={{ alignItems: 'flex-start' }}>
                                        {job.companyId?.logoUrl ? (
                                            <img
                                                src={`${API_URL}${job.companyId.logoUrl}`}
                                                className="cd-company-logo-large"
                                                alt="Company Logo"
                                            />
                                        ) : (
                                            <div className="cd-avatar-placeholder" style={{ width: 120, height: 120, fontSize: '3rem', borderRadius: '8px', background: '#3b82f6' }}>
                                                {job.company?.charAt(0) || 'C'}
                                            </div>
                                        )}
                                        <div className="cd-inst-content">
                                            <h3>{job.company || job.companyId?.name}</h3>
                                            <p>{job.companyId?.description || 'A verified partner company.'}</p>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '15px' }}>
                                                {job.companyId?.industry && <div><strong>Industry:</strong> {job.companyId.industry}</div>}
                                                {job.companyId?.size && <div><strong>Size:</strong> {job.companyId.size}</div>}
                                            </div>

                                            <div className="cd-inst-actions" style={{ marginTop: '20px' }}>
                                                {job.companyId?.website && (
                                                    <button className="btn-outline primary" onClick={() => window.open(job.companyId.website, '_blank')}>
                                                        Visit Website
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </div>

                    <div className="cd-right-column">
                        <div className="cd-price-card">
                            <div className="cd-price-amount" style={{ fontSize: '24px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                Pay: {job.salaryRange || 'Competitive Salary'}
                            </div>
                            <div className="cd-deadline" style={{ marginBottom: '20px' }}>
                                Open until filled
                            </div>

                            {!appStatus ? (
                                <button className="cd-btn-primary" onClick={handleApply}>
                                    Apply Now
                                </button>
                            ) : (
                                <div style={{
                                    padding: '14px',
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    textAlign: 'center',
                                    border: '1px solid #e2e8f0',
                                    color: '#64748b',
                                    fontWeight: 700,
                                    fontSize: '14px'
                                }}>
                                    Already Applied
                                </div>
                            )}

                            <div className="cd-training-summary">
                                <h4>Job Details</h4>
                                <div className="cd-ts-item">
                                    <Briefcase className="cd-ts-icon" size={18} />
                                    <span className={`cd-type-text ${job.type?.toLowerCase().replace(/\s+/g, '-')}`}>{job.type}</span>
                                </div>
                                <div className="cd-ts-item">
                                    <MapPin className="cd-ts-icon" size={18} />
                                    <span>{job.location}</span>
                                </div>
                                <div className="cd-ts-item">
                                    <FileText className="cd-ts-icon" size={18} />
                                    <span>Experience: {job.experience || 'Not specified'}</span>
                                </div>
                                <div className="cd-ts-item">
                                    <CheckCircle className="cd-ts-icon" size={18} />
                                    <span>Actively Hiring</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );

    return createPortal(
        <div className="course-detail-portal-wrapper" ref={scrollContainerRef}>
            {jobDetailContent}
        </div>,
        document.body
    );
};

export default PublicJobDetail;
